import { createContext, useContext } from 'react'
import { ISO8601DateTimeString } from 'lib/dto'
import { useUserContext } from 'lib/context'
import parsePhoneNumber from 'libphonenumber-js'
import { match } from 'ts-pattern'
import { useT } from 'lib/i18n-client'

type NullPropagatingFormatter<TValue, TOptions> = <TNullable extends TValue | null | undefined>(
  value: TNullable,
  options?: TOptions
) => TNullable extends null | undefined ? string | null : string

export class Formatter {
  constructor(
    readonly lang: string,
    readonly currency: string,
    readonly useCurrencySymbols = false
  ) {}

  get currencySymbol() {
    return match(this.currency)
      .with('UZS', () =>
        match(this.lang)
          .with('ru', () => 'сум')
          .with('uz', () => "so'm")
          .otherwise(() => 'UZS')
      )
      .with('BYN', 'TJS', () => this.currency)
      .otherwise(
        () =>
          new Intl.NumberFormat('en', {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'narrowSymbol',
          })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value ?? this.currency
      )
  }

  country = (code: string, options?: Omit<Intl.DisplayNamesOptions, 'type'>) =>
    code !== 'все' && 'DisplayNames' in Intl
      ? new Intl.DisplayNames(this.lang, { type: 'region', ...options }).of(code)
      : code

  date: NullPropagatingFormatter<Date | ISO8601DateTimeString, Intl.DateTimeFormatOptions> = (
    date,
    options
  ) =>
    date != null
      ? new Intl.DateTimeFormat(this.lang, options).format(new Date(date))
      : (null as any)

  number: NullPropagatingFormatter<number, Intl.NumberFormatOptions> = (number, options) =>
    number != null && !Number.isNaN(number) && Number.isFinite(number)
      ? number.toLocaleString(this.lang, options)
      : (null as any)

  phone: NullPropagatingFormatter<string, never> = (phone) =>
    phone != null ? parsePhoneNumber(phone)?.formatInternational() : (null as any)

  price: NullPropagatingFormatter<number | bigint, Intl.NumberFormatOptions> = (price, options) =>
    price != null && !Number.isNaN(price) && Number.isFinite(price)
      ? (options?.currency ?? this.currency) !== 'UZS'
        ? new Intl.NumberFormat(this.lang, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: ['RUB', 'UZS'].includes(this.currency) ? 0 : 2,
            ...options,
          })
            .formatToParts(price)
            .map((part) =>
              part.type === 'currency' &&
              this.useCurrencySymbols &&
              options?.currencyDisplay == null &&
              (options?.currency == null || options.currency === this.currency)
                ? this.currencySymbol
                : part.value
            )
            .join('')
        : (['ru', 'uz'].includes(this.lang) ? '' : 'UZS\xa0') +
          new Intl.NumberFormat(this.lang === 'uz' ? 'ru' : this.lang, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            ...options,
          }).format(price) +
          match(this.lang)
            .with('ru', () => '\xa0сум')
            .with('uz', () => "\xa0so'm")
            .otherwise(() => '')
      : (null as any)

  priceRangeToParts = (
    start: number | null,
    end: number | null,
    options?: Intl.NumberFormatOptions
  ) => {
    const showStart = start !== null
    const showEnd = showStart && end !== null && start !== end

    if (!showStart) return [null, null, null] as const

    const formattedStartPrice =
      showEnd && this.lang === 'ru'
        ? this.number(start, {
            maximumFractionDigits: this.currency === 'RUB' ? 0 : 2,
            ...options,
          })
        : this.price(start, options)

    if (!showEnd) return [formattedStartPrice, null, null] as const

    const separator = '–'
    const formattedEndPrice =
      this.lang === 'en'
        ? this.number(end, {
            maximumFractionDigits: this.currency === 'RUB' ? 0 : 2,
            ...options,
          })
        : this.price(end, options)

    return [formattedStartPrice, separator, formattedEndPrice] as const
  }

  priceRange = (start: number | null, end: number | null, options?: Intl.NumberFormatOptions) => {
    return this.priceRangeToParts(start, end, options).filter(Boolean).join('') || null
  }
}

export const CurrencyContext = createContext<string | null>(null)

export const useIntl = (currency?: string) => {
  const contextCurrency = useContext(CurrencyContext)
  const { lang, currentMall } = useUserContext()
  const t = useT() as (key: string | TemplateStringsArray, options?: object) => string

  return {
    t,
    lang,
    format: new Formatter(
      lang,
      currency ?? contextCurrency ?? currentMall.currency,
      currency != null || contextCurrency != null
    ),
  }
}
